import React, { useEffect, useState, useMemo, useRef, useCallback, useContext } from 'react';
import {
  Card, ActionsDiv, FormGrid, FormGroup, Label,
  Select, Td, TdFirst, Tr, InputLabel, Summary, SummaryItem, SummaryLabel,
  SummaryValue, Separator, ButtonsDiv, AddButton, AddButtonImg, AddButtonText,
  CancelButton, CancelButtonText, RemoveButton, RemoveButtonImg, QuantityTableWrapper,
  RemoveButtontext, PriceFormGroup, ProductPriceValue, SummaryAmount,
  SpecificationFormGroup, SpecificationInput,
} from './style';
import { useFloatingGradeNumbers } from '../../../../hooks/useFloatingGradeNumbers';
import GlobalLoadingOverlay from '../../../../components/GlobalLoadingOverlay';
import whitePlus from '../../../../assets/fashion/TableFashionHeader/whitePlus.svg';
import remove from '../../../../assets/fashion/Actions/remove.svg';
import { Outlet } from 'react-router-dom';
import axios from '../../../../config/axios';
import AutocompleteSelect from './AutoCompleteSelect';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import { useFashionEspecificacaoAutoFill } from '../RegisterFashionOrder/Hooks/useFashionEspecificacaoAutoFill';

let cachedCollections: any[] | null = null;
let cachedCollectionsPromise: Promise<any[]> | null = null;

const cachedFamiliesByCollection = new Map<number, any[]>();
const cachedGroupsByFamily = new Map<string, any[]>();

interface FashionOrderProductItemInput {
  colorId: number;
  size: string;
  quantity: number;
}


interface ColorData {
  id: number;
  name: string;
}

interface ProductApiData {
  id: number;
  fashionProductId: number;
  discount: number | null;
  sellingPrice?: number | null;
  totalItems: number;
  subtotal: number;
  items: FashionOrderProductItemInput[];
  size?: string[];

  fashionOrderId?: number;
  fashionProduct?: {
    id: number;
    name: string;
    price: number;
    size: string[];
    collection?: { id: number; name: string };
    group: { id: number; name?: string; family?: { id: number; name?: string; collectionId?: number; } };
  };
  colorCount?: number;
  colors?: string[];
  family?: { id: number; Corileone?: { name: string }; name?: string; familyId?: number; };
  group?: { id: number; name?: string };
  name?: string;
  price?: number;
  totalQuantity?: number;
  totalSubtotal?: number;
  selectedColors?: number[];
  valorFinal?: number;
  discountValue?: number;
  sizeQuantities?: { [size: string]: number };
  kitIndex?: number | null;
  isKitIntent?: boolean;
  nomeCustomizado?: string;
}

interface CollectionData {
  id: number;
  name: string;
}

interface FamilyData {
  id: number;
  name: string;
  collectionId?: number;
  groups?: GroupData[];
  colors?: ColorData[];
}

interface GroupData {
  id: number;
  name: string;
  products?: ProductApiData[];
}

interface PropsType {
  order: any[];
  setOrder: React.Dispatch<React.SetStateAction<any>>;
  product?: any;
  onClose?: () => void;
  onRemoveProduct: (index: number) => void;
  onProductUpdated?: (oldProduct: ProductApiData, updatedProduct: ProductApiData) => void;
  onProductAdded?: any;
  isEditing: boolean;
  isKitMode: boolean;
  setIsKitMode: React.Dispatch<React.SetStateAction<boolean>>;
}

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

const centsDigitsToReais = (value: unknown): number => {
  if (value == null) return 0;
  const str = String(value).replace(/\D/g, '');
  if (!str) return 0;
  return Number(str) / 100;
};

const formatCentDigitsForInput = (value: unknown): string => {
  if (value == null) return '';
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return '';
  const num = Number(digits) / 100;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
};

const BASE = process.env.REACT_APP_GATEWAY_BASE_URL;

const compareSizes = (a: string, b: string): number => {
  const isNumA = /^\d+$/.test(a);
  const isNumB = /^\d+$/.test(b);

  if (isNumA && isNumB) {
    return Number(a) - Number(b);
  }
  if (isNumA && !isNumB) {
    return -1;
  }
  if (!isNumA && isNumB) {
    return 1;
  }

  const textOrder = [
    'PP', 'P', 'M', 'G', 'GG', 'XL', 'XLL',
    'G1', 'G2', 'G3', 'G4',
    'TAMANHO ÚNICO', 'ÚNICO', 'U'
  ];

  const normA = a.trim().toUpperCase();
  const normB = b.trim().toUpperCase();

  const idxA = textOrder.indexOf(normA);
  const idxB = textOrder.indexOf(normB);

  if (idxA !== -1 && idxB !== -1) {
    return idxA - idxB;
  }
  if (idxA !== -1 && idxB === -1) {
    return -1;
  }
  if (idxA === -1 && idxB !== -1) {
    return 1;
  }

  return a.localeCompare(b);
};

const sortSizes = (sizes: string[]): string[] => {
  return [...sizes].sort(compareSizes);
};

const AddProductForm: React.FC<PropsType> = ({ order, setOrder, product, onClose, onRemoveProduct, onProductUpdated, onProductAdded, isEditing, isKitMode, setIsKitMode }) => {
  const { setFeedback } = useContext(FeedbackContext);
  const renderCountRef = useRef(0)
  const lastRenderAtRef = useRef(Date.now())
  const quantityWrapperRef = useRef<HTMLDivElement | null>(null)

  const [allProductsData, setAllProductsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  renderCountRef.current += 1
  const nowRender = Date.now()
  console.log('🔁 [AddProductForm] render', renderCountRef.current, { isEditing, productId: product?.fashionProductId, durationSinceLastRender: nowRender - lastRenderAtRef.current, allProductsLength: allProductsData.length, isLoading })
  lastRenderAtRef.current = nowRender

  const [collectionsData, setCollectionsData] = useState<CollectionData[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');
  const [familiesData, setFamiliesData] = useState<FamilyData[]>([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>('');
  const [familyGroupsData, setFamilyGroupsData] = useState<GroupData[]>([]);
  const [selectedFamilyGroupId, setSelectedFamilyGroupId] = useState<string>('');

  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  const [availableColors, setAvailableColors] = useState<ColorData[]>([]);
  const [selectedColors, setSelectedColors] = useState<{ [colorId: number]: boolean }>({});
  const [quantities, setQuantities] = useState<Map<number, { [size: string]: number }>>(new Map());
  const [discount, setDiscount] = useState<string>('');
  const [sellingPrice, setSellingPrice] = useState<string>('');
  const [specification, setSpecification] = useState<string>('');
  const [valorFinal, setValorFinal] = useState<number | null>(null);
  const valorFinalOverrideRef = useRef(false);

  const skipCascadeReset = useRef(false);
  const skipProductColorReset = useRef(false);
  const isAutoSelectingProduct = useRef(false);
  const lastSelectedProductIdRef = useRef<number | null>(null);
  const pendingFamilyGroupId = useRef<string | null>(null);
  const [pendingCreateKit, setPendingCreateKit] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const getItemColorId = (item: any): number | null => {
    if (!item || typeof item !== 'object') return null;
    const keys = ['colorId', 'color_id', 'color', 'itemColorId', 'item_color_id', 'collectionColorId', 'collection_color_id', 'corId', 'cor_id'];
    for (const key of keys) {
      if (item[key] !== undefined && item[key] !== null && item[key] !== '') {
        const parsed = Number(item[key]);
        if (!Number.isNaN(parsed)) return parsed;
      }
    }
    return null;
  };

  const getItemSize = (item: any): string => {
    if (!item || typeof item !== 'object') return '';
    const keys = ['size', 'tamanho', 'grade', 'gradeId', 'grade_id', 'sizeId', 'tam'];
    for (const key of keys) {
      const val = item[key];
      if (val !== undefined && val !== null && String(val).trim() !== '') {
        return String(val);
      }
    }
    return '';
  };

  const getItemQuantity = (item: any): number => {
    if (!item || typeof item !== 'object') return 0;
    const keys = ['quantity', 'quantidade', 'qty', 'qtd'];
    for (const key of keys) {
      const val = item[key];
      const num = Number(val);
      if (!Number.isNaN(num)) return num;
    }
    return 0;
  };

  const calculatedTotalQuantity = useMemo(() => {
    let sum = 0;
    quantities.forEach(sizeMap => Object.values(sizeMap).forEach(qty => (sum += qty)));
    return sum;
  }, [quantities]);

  const totalSelectedColors = useMemo(() => Object.values(selectedColors).filter(Boolean).length, [selectedColors]);


  const calculatedTotalSubtotal = useMemo(() => {
    if (selectedPrice === null) return 0;
    return calculatedTotalQuantity * selectedPrice;
  }, [calculatedTotalQuantity, selectedPrice]);


  const parsedDiscount = useMemo(() => {
    const parsed = parseFloat(String(discount).replace(',', '.'));
    return isNaN(parsed) ? null : parsed;
  }, [discount]);

  const computedValorFinal = useMemo(() => {
    const venda = centsDigitsToReais(sellingPrice);
    if (venda > 0) return venda;
    if (valorFinal !== null) return valorFinal;
    if (parsedDiscount !== null && parsedDiscount !== 0) {
      return calculatedTotalSubtotal - (parsedDiscount / 100) * calculatedTotalSubtotal;
    }
    return calculatedTotalSubtotal;
  }, [sellingPrice, valorFinal, calculatedTotalSubtotal, parsedDiscount]);

  const descontoEmReais = calculatedTotalSubtotal - computedValorFinal;

  const descontoPercentual = useMemo(() => {
    if (calculatedTotalSubtotal <= 0) return '—';
    const pct = (descontoEmReais / calculatedTotalSubtotal) * 100;
    const texto = `${Math.abs(pct).toFixed(1).replace('.', ',')}%`;
    return pct < 0 ? `+${texto}` : texto;
  }, [descontoEmReais, calculatedTotalSubtotal]);

  /* O percentual guarda 2 casas: sem saber quem foi editado por último, a ida e volta reescreve o
     preço digitado a partir de subtotal R$ 200. */
  const fonteRef = useRef<'venda' | 'desconto' | null>(null);

  const percentualDe = useCallback(
    (venda: number): number => ((calculatedTotalSubtotal - venda) / calculatedTotalSubtotal) * 100,
    [calculatedTotalSubtotal],
  );

  const escreveDesconto = useCallback(
    (pct: number) => setDiscount(pct === 0 ? '' : String(Number(pct.toFixed(2))).replace('.', ',')),
    [],
  );

  const handleSellingPriceChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 12);
    fonteRef.current = 'venda';
    setSellingPrice(digits);
    if (!digits) { setDiscount(''); return; }
    if (calculatedTotalSubtotal <= 0) return;
    escreveDesconto(percentualDe(centsDigitsToReais(digits)));
  };

  const handleDiscountChange = (raw: string) => {
    fonteRef.current = 'desconto';
    setDiscount(raw);
    if (!raw.trim()) { setSellingPrice(''); return; }
    const pct = parseFloat(raw.replace(',', '.'));
    /* `centsDigitsToReais` descarta o sinal, então acima de 100% o preço voltaria positivo. */
    if (isNaN(pct) || calculatedTotalSubtotal <= 0) return;
    const venda = calculatedTotalSubtotal * (1 - Math.min(pct, 100) / 100);
    setSellingPrice(String(Math.max(0, Math.round(venda * 100))));
  };

  useEffect(() => {
    if (calculatedTotalSubtotal <= 0) return;
    const venda = centsDigitsToReais(sellingPrice);

    if (fonteRef.current === 'venda') {
      if (venda > 0) escreveDesconto(percentualDe(venda));
      return;
    }
    if (fonteRef.current === 'desconto') {
      if (parsedDiscount === null) return;
      const alvo = Math.round(calculatedTotalSubtotal * (1 - Math.min(parsedDiscount, 100) / 100) * 100);
      setSellingPrice((atual) => (Number(atual || 0) === alvo ? atual : String(Math.max(0, alvo))));
      return;
    }
    if (parsedDiscount === null && venda > 0) escreveDesconto(percentualDe(venda));
  }, [calculatedTotalSubtotal, sellingPrice, parsedDiscount, escreveDesconto, percentualDe]);

  useEffect(() => {
    if (!valorFinalOverrideRef.current) {
      setValorFinal(null);
    }
  }, [calculatedTotalQuantity, selectedPrice, discount]);

  const selectedProductName = useMemo(
    () => allProductsData.find(p => p.id === selectedProductId)?.name,
    [allProductsData, selectedProductId],
  );

  useFashionEspecificacaoAutoFill({
    productName: selectedProductName,
    value: specification,
    onChange: setSpecification,
  });

  const alreadyAddedProductIds = useMemo(() => {
    const ids = new Set<number>();
    order.forEach(item => {
      const id = Number(item.fashionProductId ?? item.id ?? item?.fashionProduct?.id);
      if (!Number.isNaN(id) && id > 0) ids.add(id);
    });
    console.debug('[AddProductForm] alreadyAddedProductIds computed', { orderLength: order.length, ids: Array.from(ids) });
    return ids;
  }, [order]);

  const filteredProducts = useMemo(() => {
    let prods = allProductsData.filter(p => p.status !== false);

    console.debug('[filteredProducts] start', {
      allProductsCount: allProductsData.length,
      selectedCollectionId,
      selectedFamilyId,
      selectedFamilyGroupId,
      allProductsSample: allProductsData.slice(0, 2),
      alreadyAddedProductIds: Array.from(alreadyAddedProductIds),
    });

    const editingProductId = Number(product?.fashionProductId ?? product?.fashionProduct?.id) || null;
    const excludedProductIds = new Set(alreadyAddedProductIds);
    if (editingProductId) excludedProductIds.delete(editingProductId);
    if (selectedProductId) excludedProductIds.delete(selectedProductId);
    if (excludedProductIds.size > 0) {
      const before = prods.length;
      prods = prods.filter(p => !excludedProductIds.has(p.id));
      console.debug('[filteredProducts] after excluding already added products', { before, after: prods.length, excludedProductIds: Array.from(excludedProductIds) });
    }

    if (selectedCollectionId) {
      const before = prods.length;
      prods = prods.filter(p => p.collectionId === parseInt(selectedCollectionId));
      console.debug('[filteredProducts] after collection filter', { before, after: prods.length });
    }

    if (selectedFamilyId) {
      const before = prods.length;
      prods = prods.filter(p => p.episodeId === parseInt(selectedFamilyId));
      console.debug('[filteredProducts] after family filter', { before, after: prods.length });
    }

    if (selectedFamilyGroupId) {
      const before = prods.length;
      prods = prods.filter(p => p.groupId === parseInt(selectedFamilyGroupId));
      console.debug('[filteredProducts] after family group filter', { before, after: prods.length });
    }

    if (selectedProductId) {
      const hasSelected = prods.some(p => p.id === selectedProductId);
      if (!hasSelected) {
        const selectedProduct = allProductsData.find(p => p.id === selectedProductId);
        if (selectedProduct) {
          prods = [selectedProduct, ...prods];
        }
      }
    }

    console.debug('[filteredProducts] final count', prods.length);
    return prods;
  }, [allProductsData, selectedCollectionId, selectedFamilyId, selectedFamilyGroupId, selectedProductId, alreadyAddedProductIds, product]);

  const availableSizes = useMemo(() => {
    if (!selectedProductId) return [];

    const p = allProductsData.find(prod => prod.id === selectedProductId);

    const sizesFromBack = (() => {
      if (!p) return [] as string[];
      const fromGrades: string[] = ((p as any).GradeInProduct || []).map((g: any) => g.Grade?.name).filter(Boolean);
      if (fromGrades.length > 0) return fromGrades;
      return (p.size as string[]) || [];
    })();

    const sizesFromItems = (() => {
      if (!product || !Array.isArray(product.items)) return [] as string[];
      const set = new Set<string>();
      product.items.forEach((item: any) => {
        const size = String(item.size || '').trim();
        if (size) set.add(size);
      });
      return Array.from(set);
    })();

    const merged = Array.from(new Set([...sizesFromBack, ...sizesFromItems]));
    if (merged.length === 0) {
      console.log('[AddProductForm] availableSizes vazio. sizesFromBack:', sizesFromBack, 'sizesFromItems:', sizesFromItems, 'product.items:', product?.items);
    } else {
      console.log('[AddProductForm] availableSizes calculado:', merged, { sizesFromBack, sizesFromItems });
    }

    const getInitialQtyForSize = (sizeName: string): number => {
      if (!product || !Array.isArray(product.items)) return 0;
      let sum = 0;
      product.items.forEach((item: any) => {
        const itemSize = getItemSize(item);
        if (itemSize.trim().toUpperCase() === sizeName.trim().toUpperCase()) {
          sum += getItemQuantity(item);
        }
      });
      return sum;
    };

    const sorted = [...merged].sort(compareSizes);

    return sorted;
  }, [selectedProductId, allProductsData, product]);

  useFloatingGradeNumbers({
    wrapperRef: quantityWrapperRef,
    rowSelector: 'tbody tr',
    labelSelector: '.size-label',
    trigger: `${availableColors.length}-${availableSizes.length}`,
    debugKey: 'FashionOrders/AddProductForm',
  })

  useEffect(() => {
    console.log('[AddProductForm] availableColors/availableSizes update', {
      availableColorsLength: availableColors.length,
      availableSizesLength: availableSizes.length,
      availableColors,
      availableSizes,
      selectedColors,
    });
  }, [availableColors, availableSizes, selectedColors]);

  useEffect(() => {
    const timerLabel = `[AddProductForm] loadProductsAndCollections ${Date.now()}`
    console.time(timerLabel)
    let mounted = true;
    setIsLoading(true);

    const loadProducts = async () => {
      const productTimer = `[AddProductForm] fetchProducts ${Date.now()}`
      console.time(productTimer)
      try {
        const res = await axios.get(`${BASE}/backend-fashion-orders/fashion-product?minimal=true`)
        const prods = Array.isArray(res.data) ? res.data : [];
        console.log('📦 [AddProductForm] allProductsData carregado:', prods.length, 'produtos. Exemplo:', prods[0]);
        return prods;
      } catch (err) {
        console.error('📦 [AddProductForm] erro ao carregar produtos:', err);
        return [] as any[];
      } finally {
        console.timeEnd(productTimer)
      }
    };

    const loadCollections = async () => {
      if (cachedCollections && cachedCollections.length === 0) {
        console.log('📦 [AddProductForm] cache de coleções vazio detectado, forçando recarga');
        cachedCollections = null;
        cachedCollectionsPromise = null;
      }

      if (cachedCollections) {
        console.log('📦 [AddProductForm] usando cache de coleções', cachedCollections.length);
        return cachedCollections;
      }
      if (cachedCollectionsPromise) {
        console.log('📦 [AddProductForm] usando promise em andamento para coleções');
        return cachedCollectionsPromise;
      }

      const collectionTimer = `[AddProductForm] fetchCollections ${Date.now()}`
      console.time(collectionTimer)

      cachedCollectionsPromise = axios
        .get(`${BASE}/backend-fashion-orders/fashion-product/collections`)
        .then(res => {
          const cols = Array.isArray(res.data) ? res.data : [];
          console.log('📦 [AddProductForm] collectionsData carregado:', cols.length, 'coleções:', cols);
          cachedCollections = cols;
          return cols;
        })
        .catch((err) => {
          console.error('📦 [AddProductForm] erro ao carregar coleções:', err);
          cachedCollections = [];
          return [] as any[];
        })
        .finally(() => {
          console.timeEnd(collectionTimer)
        });

      return cachedCollectionsPromise;
    };

    const load = async () => {
      try {
        const [prods, cols] = await Promise.all([loadProducts(), loadCollections()]);
        if (!mounted) return;
        console.log('📦 [AddProductForm] loadProductsAndCollections finalizado', { prodsCount: prods.length, colsCount: cols.length });
        setAllProductsData(prods);
        setCollectionsData(cols);
      } catch (err) {
        console.error('📦 [AddProductForm] erro no loadProductsAndCollections:', err);
      } finally {
        if (mounted) setIsLoading(false);
        console.timeEnd(timerLabel);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const [isLoadingFamilies, setIsLoadingFamilies] = useState(false);

  useEffect(() => {
    if (isAutoSelectingProduct.current) {
      if (selectedCollectionId) {
        const collectionIdNum = Number(selectedCollectionId);
        const coll = collectionsData.find(c => Number(c.id) === collectionIdNum);
        if (coll) {
          const episodes = (coll as any).Episode || [];
          setFamiliesData(episodes.map((ep: any) => ({ id: ep.id, name: ep.name })));
        } else {
          setFamiliesData([]);
        }
      }
      return;
    }

    setSelectedFamilyId('');
    setFamiliesData([]);
    setFamilyGroupsData([]);
    setSelectedFamilyGroupId('');
    setSelectedProductId(null);
    setSelectedPrice(null);
    setAvailableColors([]);
    setSelectedColors({});
    setQuantities(new Map());

    if (!selectedCollectionId) return;

    const collectionIdNum = Number(selectedCollectionId);
    const coll = collectionsData.find(c => Number(c.id) === collectionIdNum);
    if (coll) {
      const episodes = (coll as any).Episode || [];
      setFamiliesData(episodes.map((ep: any) => ({ id: ep.id, name: ep.name })));
    }
  }, [selectedCollectionId, collectionsData]);

  useEffect(() => {
    if (isAutoSelectingProduct.current) {
      if (selectedCollectionId) {
        const fetchGroups = async () => {
          const collectionIdNum = Number(selectedCollectionId);
          const res = await axios.get(`${BASE}/backend-fashion-orders/fashion-product/groups?collectionId=${collectionIdNum}`);
          return Array.isArray(res.data) ? res.data : [];
        };
        fetchGroups()
          .then(groups => setFamilyGroupsData(groups))
          .catch(console.error);
      }
      return;
    }

    setSelectedFamilyGroupId('');
    setFamilyGroupsData([]);
    setSelectedProductId(null);
    setSelectedPrice(null);
    setAvailableColors([]);
    setSelectedColors({});
    setQuantities(new Map());

    if (!selectedCollectionId) return;

    const fetchGroups = async () => {
      const collectionIdNum = Number(selectedCollectionId);
      const res = await axios.get(`${BASE}/backend-fashion-orders/fashion-product/groups?collectionId=${collectionIdNum}`);
      return Array.isArray(res.data) ? res.data : [];
    };

    fetchGroups()
      .then(groups => {
        setFamilyGroupsData(groups);
      })
      .catch(console.error);
  }, [selectedCollectionId]);

  useEffect(() => {
    if (isAutoSelectingProduct.current) return;

    setSelectedProductId(null);
    setSelectedPrice(null);
    setAvailableColors([]);
    setSelectedColors({});
    setQuantities(new Map());
  }, [selectedFamilyId]);

  useEffect(() => {
    if (isAutoSelectingProduct.current) return;

    setSelectedProductId(null);
    setSelectedPrice(null);
    setAvailableColors([]);
    setSelectedColors({});
    setQuantities(new Map());
  }, [selectedFamilyGroupId]);

  useEffect(() => {
    console.log('🧠 [AddProductForm] useEffect(isEditing/product/allProductsData)', { isEditing, productId: product?.fashionProductId, allProductsLength: allProductsData.length });
    if (!isEditing || !product || allProductsData.length === 0) return;

    console.time('[AddProductForm] sync product selection');
    const productId = product.fashionProductId || product.fashionProduct?.id;
    if (!productId) {
      console.timeEnd('[AddProductForm] sync product selection');
      return;
    }

    const found = allProductsData.find(p => p.id === productId);

    const needsFullFetch = Boolean(
      productId &&
      (!found ||
        !Array.isArray((found as any).ProductItemColor) ||
        (found as any).ProductItemColor.length === 0 ||
        !Array.isArray((found as any).GradeInProduct) ||
        ((found as any).GradeInProduct.length === 0 && !(found as any).size?.length))
    );

    if (needsFullFetch && productId) {
      fetchFullProduct(productId).catch(console.error);
    }

    console.log('[AddProductForm] sync product selection data', {
      productId,
      found: Boolean(found),
      foundId: found?.id,
      productColors: product.colors,
      productSelectedColors: product.selectedColors,
      productItemsCount: Array.isArray(product.items) ? product.items.length : 0,
    });
    console.log(
      '[AddProductForm] sync product raw data',
      JSON.stringify(
        {
          itemKeys: Array.isArray(product.items) && product.items.length > 0 ? Object.keys(product.items[0]) : [],
          itemsCount: Array.isArray(product.items) ? product.items.length : 0,
          colors: product.colors,
          selectedColors: product.selectedColors,
          itemSample: product.items && product.items.length > 0 ? product.items[0] : null,
        },
        null,
        2,
      ),
    );

    isAutoSelectingProduct.current = true;
    skipProductColorReset.current = true;
    setSelectedProductId(productId);
    lastSelectedProductIdRef.current = productId;
    setSelectedPrice(found?.price ?? product.fashionProduct?.price ?? 0);
    setDiscount(String(product.discount || ''));
    setSellingPrice(product.sellingPrice ? String(Math.round(Number(product.sellingPrice) * 100)) : '');
    setSpecification(String(product.nomeCustomizado || ''));
    setValorFinal(product.valorFinal ?? null);
    if (found?.collectionId) setSelectedCollectionId(String(found.collectionId));
    if (found?.episodeId) {
      setSelectedFamilyId(String(found.episodeId));
    } else if (found?.familyId) {
      setSelectedFamilyId(String(found.familyId));
    }
    if (found?.groupId) {
      setSelectedFamilyGroupId(String(found.groupId));
    }

    setTimeout(() => {
      isAutoSelectingProduct.current = false;
    }, 200);

    const syncAvailableColorsFromProduct = () => {
      const colorMap = new Map<number, string>();

      const addColor = (id: number, name: string) => {
        if (Number.isNaN(id)) return;
        if (!colorMap.has(id)) colorMap.set(id, name);
      };

      if (Array.isArray(product.selectedColors)) {
        const colorsArray = Array.isArray(product.colors) ? product.colors : [];
        product.selectedColors.forEach((c: any, index: number) => {
          const id = Number(c);
          if (Number.isNaN(id)) return;

          let name = '';
          const maybe = colorsArray[index];
          if (maybe !== undefined && maybe !== null) {
            if (typeof maybe === 'object') {
              name = String(maybe.name ?? maybe.label ?? maybe.value ?? id);
            } else {
              name = String(maybe);
            }
          }
          if (!name) name = String(id);
          addColor(id, name);
        });
      }

      ((found as any)?.ProductItemColor || []).forEach((pic: any) => {
        const id = pic?.CollectionColor?.id ?? pic?.collectionColorId;
        if (id === undefined || id === null) return;
        const name = pic?.CollectionColor?.name ?? String(id);
        addColor(Number(id), name);
      });

      (product.items || [])
        .map((item: any) => getItemColorId(item))
        .filter((id: number | null) => id !== null)
        .forEach((id: number | null) => {
          const numericId = Number(id);
          if (Number.isNaN(numericId)) return;
          if (!colorMap.has(numericId)) {
            addColor(numericId, String(numericId));
          }
        });

      const productColors: ColorData[] = Array.from(colorMap.entries()).map(([id, name]) => ({ id, name }));
      setAvailableColors(productColors);

      if (Array.isArray(product.selectedColors) && product.selectedColors.length > 0) {
        const selectedMap: { [colorId: number]: boolean } = {};
        product.selectedColors.forEach((colorId: any) => {
          const parsed = Number(colorId);
          if (!Number.isNaN(parsed)) selectedMap[parsed] = true;
        });
        setSelectedColors(selectedMap);
      }
    };

    if (found) {
      syncAvailableColorsFromProduct();
    } else {
      syncAvailableColorsFromProduct();
    }

    console.timeEnd('[AddProductForm] sync product selection');

    if (product.items && Array.isArray(product.items)) {
      const initColors: { [id: number]: boolean } = {};
      const initQty = new Map<number, { [size: string]: number }>();
      product.items.forEach((item: any) => {
        const colorId = Number(item.colorId);
        const size = String(item.size || '');
        if (Number.isNaN(colorId) || !size) return;

        initColors[colorId] = true;
        if (!initQty.has(colorId)) initQty.set(colorId, {});
        initQty.get(colorId)![size] = Number(item.quantity) || 0;
      });
      setSelectedColors(initColors);
      setQuantities(initQty);
    }
  }, [isEditing, product, allProductsData]);

  useEffect(() => {
    console.log('[AddProductForm] selectedProductId effect', {
      selectedProductId,
      skipProductColorReset: skipProductColorReset.current,
      lastSelectedProductId: lastSelectedProductIdRef.current,
      availableColorsLength: availableColors.length,
    });

    if (skipProductColorReset.current) {
      skipProductColorReset.current = false;
      return;
    }

    if (selectedProductId === lastSelectedProductIdRef.current && availableColors.length > 0) {
      return;
    }

    setAvailableColors([]);
    setSelectedColors({});
    setQuantities(new Map());

    if (!selectedProductId) {
      lastSelectedProductIdRef.current = selectedProductId;
      return;
    }

    const found = allProductsData.find(p => p.id === selectedProductId);
    if (!found) {
      lastSelectedProductIdRef.current = selectedProductId;
      return;
    }

    const seen = new Set<number>();
    const productColors: ColorData[] = ((found as any).ProductItemColor || [])
      .map((pic: any) => ({
        id: pic.CollectionColor?.id ?? pic.collectionColorId,
        name: pic.CollectionColor?.name ?? String(pic.collectionColorId),
      }))
      .filter((c: ColorData) => {
        if (seen.has(c.id)) return false;
        seen.add(c.id);
        return true;
      });

    setAvailableColors(productColors);
    lastSelectedProductIdRef.current = selectedProductId;
  }, [selectedProductId, allProductsData]);

  const fetchFullProduct = async (id: number) => {
    try {
      const res = await axios.get(`${BASE}/backend-fashion-orders/fashion-product/${id}`);
      const prod = res.data;
      setAllProductsData(prev => {
        const idx = prev.findIndex(p => p.id === prod.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = prod;
          return next;
        }
        return [prod, ...prev];
      });
      return prod;
    } catch (err) {
      console.error('[AddProductForm] erro ao buscar produto completo:', err);
      return null;
    }
  };

  const handleProductSelectChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value) || null;
    console.log('[AddProductForm] Produto selecionado id:', id);

    setAvailableColors([]);
    setSelectedColors({});
    setQuantities(new Map());
    setValorFinal(null);

    let p = id ? allProductsData.find(prod => prod.id === id) : null;

    // Some API responses (when using minimal=true) do not return colors/sizes.
    // Fetch full product details when needed.
    const needsFullFetch = Boolean(
      id &&
      (!p ||
        !Array.isArray((p as any).ProductItemColor) ||
        (p as any).ProductItemColor.length === 0 ||
        !Array.isArray((p as any).GradeInProduct) ||
        ((p as any).GradeInProduct.length === 0 && !(p as any).size?.length))
    );

    if (needsFullFetch && id) {
      const full = await fetchFullProduct(id);
      if (full) {
        p = full;
      }
    }

    console.log('[AddProductForm] Produto encontrado:', p);
    console.log('[AddProductForm] familyId:', p?.familyId, '| Family:', (p as any)?.Family, '| family:', (p as any)?.family, '| size:', p?.size, '| price:', p?.price);

    setSelectedPrice(p?.price ?? 0);
    setSelectedProductId(id);
    setQuantities(new Map());
    setSelectedColors({});

    if (p) {
      isAutoSelectingProduct.current = true;

      setSelectedCollectionId(p.collectionId ? String(p.collectionId) : '');
      setSelectedFamilyId(p.episodeId ? String(p.episodeId) : '');
      setSelectedFamilyGroupId(p.groupId ? String(p.groupId) : '');

      setTimeout(() => {
        isAutoSelectingProduct.current = false;
      }, 200);
    } else {
      setSelectedCollectionId('');
      setSelectedFamilyId('');
      setSelectedFamilyGroupId('');
      setSelectedProductId(null);
      setSelectedPrice(0);
    }
  };

  const buildItemsToSubmit = () => {
    const items: FashionOrderProductItemInput[] = [];
    let total = 0;

    quantities.forEach((sizeMap, colorId) => {
      if (!availableColors.find(c => c.id === colorId)) return;
      Object.entries(sizeMap).forEach(([size, quantity]) => {
        if (quantity > 0) {
          items.push({ colorId, size, quantity });
          total += quantity;
        }
      });
    });

    Object.keys(selectedColors).forEach(k => {
      const colorId = parseInt(k);
      if (selectedColors[colorId] && !items.some(i => i.colorId === colorId)) {
        items.push({ colorId, size: 'N/A', quantity: 0 });
      }
    });

    items.sort((a, b) => compareSizes(a.size, b.size));

    return { items, total };
  };

  const resolveFamilyForPayload = useCallback(
    (prod: any): { id: number; name: string; collectionId?: number } | undefined => {
      const list = familiesData || [];
      const byId = (id: number) => list.find((f) => Number(f.id) === id);

      const rel = prod?.Family ?? prod?.family;
      if (rel) {
        const id = Number(rel.id ?? prod?.familyId);
        const name = String(rel.name ?? '').trim();
        if (name && !Number.isNaN(id)) {
          return { id, name, collectionId: rel.collectionId ?? prod?.collectionId };
        }
        if (!Number.isNaN(id)) {
          const hit = byId(id);
          if (hit?.name) return { id, name: hit.name, collectionId: hit.collectionId };
        }
      }
      const fid = prod?.familyId != null ? Number(prod.familyId) : NaN;
      if (!Number.isNaN(fid)) {
        const hit = byId(fid);
        if (hit?.name) return { id: fid, name: hit.name, collectionId: hit.collectionId };
      }
      if (selectedFamilyId) {
        const sid = Number(selectedFamilyId);
        if (!Number.isNaN(sid)) {
          const hit = byId(sid);
          if (hit?.name) return { id: sid, name: hit.name, collectionId: hit.collectionId };
        }
      }
      return undefined;
    },
    [familiesData, selectedFamilyId],
  );

  const buildFashionProductPayload = (prod: any) => {
    const collectionFromDropdown = collectionsData.find(c => c.id === parseInt(selectedCollectionId));

    console.log('🏗️ [buildFashionProductPayload] prod recebido:', prod);
    console.log('🏗️ [buildFashionProductPayload] prod.Collection:', prod.Collection);
    console.log('🏗️ [buildFashionProductPayload] prod.Family?.Collection:', prod.Family?.Collection);
    console.log('🏗️ [buildFashionProductPayload] selectedCollectionId:', selectedCollectionId);
    console.log('🏗️ [buildFashionProductPayload] collectionFromDropdown:', collectionFromDropdown);
    console.log('🏗️ [buildFashionProductPayload] collectionsData:', collectionsData);

    const resolvedCollection =
      prod.Collection
        ? { id: prod.Collection.id, name: prod.Collection.name }
        : prod.Family?.Collection
          ? { id: prod.Family.Collection.id, name: prod.Family.Collection.name }
          : collectionFromDropdown
            ? { id: collectionFromDropdown.id, name: collectionFromDropdown.name }
            : undefined;

    console.log('🏗️ [buildFashionProductPayload] resolvedCollection:', resolvedCollection);

    const resolvedFamily = resolveFamilyForPayload(prod);

    return ({
      id: prod.id,
      name: prod.name || '',
      price: selectedPrice ?? 0,
      size: prod.size || [],
      ncmId: prod.ncmId ?? null,
      collection: resolvedCollection,
      group: {
        id: prod.Group?.id || prod.groupId || 0,
        name: prod.Group?.name || '',
        family: resolvedFamily
          ? {
            id: resolvedFamily.id,
            name: resolvedFamily.name,
            collectionId: resolvedFamily.collectionId ?? prod.collectionId,
          }
          : undefined,
      },
    });
  };

  const resetForm = (keepKitMode = false) => {
    setSelectedProductId(null);
    setSelectedCollectionId('');
    setSelectedFamilyId('');
    setSelectedFamilyGroupId('');
    setSelectedPrice(null);
    setAvailableColors([]);
    setSelectedColors({});
    setQuantities(new Map());
    setDiscount('');
    setSellingPrice('');
    setSpecification('');
    if (!keepKitMode) {
      setIsKitMode(false);
    }
  };

  const handleAddProduct = (isKit: boolean) => {
    const selectedProduct = allProductsData.find(p => p.id === selectedProductId);

    if (!isKit) {
      if (!selectedProduct) {
        setFeedback({ isOpen: true, message: 'Selecione um produto antes de adicionar ao pedido.', statusOk: false });
        return;
      }
      if (totalSelectedColors === 0) {
        setFeedback({ isOpen: true, message: 'O produto não tem cores da coleção vinculada.', statusOk: false });
        return;
      }
    }

    if (isKit && !selectedProduct) { if (onProductAdded) { onProductAdded({ isKitIntent: true } as ProductApiData); } resetForm(); return; }

    const alreadyAdded = order.some(item => {
      const id = Number(item.fashionProductId ?? item.id ?? item?.fashionProduct?.id);
      return id === selectedProduct.id;
    });
    if (alreadyAdded) {
      setFeedback({ isOpen: true, message: 'Este produto já foi adicionado ao pedido.', statusOk: false });
      return;
    }

    const { items: itemsToSubmit, total: calculatedTotalItems } = buildItemsToSubmit();

    const resolvedFam = resolveFamilyForPayload(selectedProduct);

    const newProductToAdd: ProductApiData = {
      id: 0,
      fashionProductId: selectedProduct.id,
      discount: parsedDiscount ?? 0,
      sellingPrice: sellingPrice ? centsDigitsToReais(sellingPrice) : null,
      totalItems: calculatedTotalItems,
      subtotal: calculatedTotalSubtotal,
      valorFinal: computedValorFinal,
      items: itemsToSubmit,
      selectedColors: Object.keys(selectedColors)
        .filter(k => selectedColors[parseInt(k)])
        .map(k => parseInt(k)),
      colors: availableColors.filter(c => selectedColors[c.id]).map(c => c.name),
      fashionProduct: buildFashionProductPayload(selectedProduct),
      family: resolvedFam
        ? { id: resolvedFam.id, name: resolvedFam.name, familyId: resolvedFam.id }
        : undefined,
      group: selectedProduct.Group
        ? { id: selectedProduct.Group.id, name: selectedProduct.Group.name }
        : undefined,
      fashionOrderId: product?.fashionOrderId,
      nomeCustomizado: specification.trim() || undefined,
      isKitIntent: isKit
    };

    console.log('✅ [handleAddProduct] newProductToAdd montado:', newProductToAdd);
    console.log('✅ [handleAddProduct] fashionProduct.collection:', newProductToAdd.fashionProduct?.collection);

    if (onProductAdded) {
      onProductAdded(newProductToAdd);
    } else {
      setOrder((prev: any[]) => [...prev, newProductToAdd]);
    }

    setSelectedProductId(null);
    setSelectedCollectionId('');
    setSelectedFamilyId('');
    setSelectedFamilyGroupId('');
    setSelectedPrice(null);
    setAvailableColors([]);
    setSelectedColors({});
    setQuantities(new Map());
    setDiscount('');
    resetForm(isKit);
    /* onClose?.(); */
  };

  const handleAddStandaloneProduct = () => {

    handleAddProduct(false);

  };

  const handleAddToKit = () => {
    const selectedProduct = allProductsData.find(p => p.id === selectedProductId)

    if (!selectedProduct) {
      alert('Selecione um produto para adicionar ao conjunto.');
      return;
    }

    const { items: itemsToSubmit, total: calculatedTotalItems } = buildItemsToSubmit();

    const resolvedFam = resolveFamilyForPayload(selectedProduct);

    const newProductToAdd: ProductApiData = {
      id: 0,
      fashionProductId: selectedProduct.id,
      discount: parsedDiscount ?? 0,
      sellingPrice: sellingPrice ? centsDigitsToReais(sellingPrice) : null,
      totalItems: calculatedTotalItems,
      subtotal: calculatedTotalSubtotal,
      valorFinal: computedValorFinal,
      items: itemsToSubmit,
      selectedColors: Object.keys(selectedColors)
        .filter(k => selectedColors[parseInt(k)])
        .map(k => parseInt(k)),
      colors: availableColors.filter(c => selectedColors[c.id]).map(c => c.name),
      fashionProduct: buildFashionProductPayload(selectedProduct),
      family: resolvedFam
        ? { id: resolvedFam.id, name: resolvedFam.name, familyId: resolvedFam.id }
        : undefined,
      group: selectedProduct.Group
        ? { id: selectedProduct.Group.id, name: selectedProduct.Group.name }
        : undefined,
      nomeCustomizado: specification.trim() || undefined,
      isKitIntent: true
    };

    if (onProductAdded) {
      onProductAdded(newProductToAdd);
    } else {
      console.warn('⚠️ [KIT] onProductAdded NÃO está definido');
    }

    resetForm(true);
  };

  const handleAddKitToOrder = () => {

    if (onProductAdded) {
      onProductAdded({ isKitIntent: true } as ProductApiData);
    } else {
      console.warn('⚠️ [KIT->ORDER] onProductAdded NÃO está definido');
    }

    resetForm();

    setIsKitMode(false);
  };

  const buildUpdatedProduct = () => {
    const selectedProductData = allProductsData.find(p => p.id === selectedProductId);
    if (!selectedProductData) return null;

    const { items: itemsToSubmit, total: calculatedTotalItems } = buildItemsToSubmit();

    const resolvedFamUpd = resolveFamilyForPayload(selectedProductData);

    return {
      ...(product as ProductApiData),
      fashionProductId: selectedProductData.id,
      discount: parsedDiscount ?? 0,
      sellingPrice: sellingPrice ? centsDigitsToReais(sellingPrice) : null,
      totalItems: calculatedTotalItems,
      subtotal: calculatedTotalSubtotal,
      valorFinal: computedValorFinal,
      items: itemsToSubmit,
      selectedColors: Object.keys(selectedColors)
        .filter(k => selectedColors[parseInt(k)])
        .map(k => parseInt(k)),
      colors: availableColors.filter(c => selectedColors[c.id]).map(c => c.name),
      fashionProduct: {
        ...(product?.fashionProduct),
        ...buildFashionProductPayload(selectedProductData),
      },
      family: resolvedFamUpd
        ? { id: resolvedFamUpd.id, name: resolvedFamUpd.name, familyId: resolvedFamUpd.id }
        : undefined,
      group: selectedProductData.Group
        ? { id: selectedProductData.Group.id, name: selectedProductData.Group.name }
        : undefined,
      nomeCustomizado: specification.trim() || undefined,
    } as ProductApiData;
  };

  const areProductsEqual = (a: ProductApiData, b: ProductApiData) => {
    if (!a || !b) return false;
    const safe = (x: any) => (x === undefined || x === null ? '' : x);
    const sameItems = JSON.stringify(a.items || []) === JSON.stringify(b.items || []);
    const sameSelectedColors = JSON.stringify(a.selectedColors || []) === JSON.stringify(b.selectedColors || []);
    const sameColors = JSON.stringify(a.colors || []) === JSON.stringify(b.colors || []);
    return (
      safe(a.totalItems) === safe(b.totalItems) &&
      safe(a.subtotal) === safe(b.subtotal) &&
      safe(a.valorFinal) === safe(b.valorFinal) &&
      sameItems &&
      sameSelectedColors &&
      sameColors
    );
  };


  const handleUpdateProduct = () => {
    if (totalSelectedColors === 0) {
      setFeedback({ isOpen: true, message: 'O produto não tem cores da coleção vinculada.', statusOk: false });
      return;
    }

    const updatedProduct = buildUpdatedProduct();
    if (!updatedProduct) {
      setFeedback({ isOpen: true, message: 'Selecione um produto válido.', statusOk: false });
      return;
    }

    if (onProductUpdated) {
      console.log('✅ [handleUpdateProduct] updatedProduct montado:', updatedProduct);
      console.log('✅ [handleUpdateProduct] fashionProduct.collection:', updatedProduct.fashionProduct?.collection);
      onProductUpdated(product, updatedProduct);
    }
    onClose?.();
  };

  if (isLoading) {
    return <GlobalLoadingOverlay />;
  }

  return (
    <>
      <Card key={formKey}>
        <FormGrid className="grid grid-cols-6 gap-4 mt-4" style={{ flexWrap: 'nowrap' }}>
          <FormGroup style={{ flex: '1 1 12%', minWidth: '90px' }}>
            <AutocompleteSelect
              label="Coleção"
              required={true}
              value={selectedCollectionId}
              onChange={(val) => setSelectedCollectionId(String(val || ''))}
              options={collectionsData}
            />
          </FormGroup>

          <FormGroup style={{ flex: '1 1 12%', minWidth: '90px' }}>
            <AutocompleteSelect
              label="Episódio"
              value={selectedFamilyId}
              onChange={(val) => setSelectedFamilyId(String(val || ''))}
              options={familiesData}
              disabled={!selectedCollectionId}
            />
          </FormGroup>

          <FormGroup style={{ flex: '1 1 12%', minWidth: '90px' }}>
            <AutocompleteSelect
              label="Grupo"
              value={selectedFamilyGroupId}
              onChange={(val) => setSelectedFamilyGroupId(String(val || ''))}
              options={familyGroupsData}
              disabled={!selectedCollectionId}
            />
          </FormGroup>

          <FormGroup style={{ flex: '2 1 25%', minWidth: '180px' }}>
            <AutocompleteSelect
              label="Produto"
              required={true}
              value={selectedProductId}
              onChange={(val) => {
                handleProductSelectChange({
                  target: { value: val }
                } as any);
              }}
              options={filteredProducts}
              disabled={false}
            />
          </FormGroup>

          {selectedProductId && (
            <>
              <PriceFormGroup style={{ flex: '1.2 1 15%', minWidth: '120px' }}>
                <Label>Preço da venda <span style={{ color: '#e53e3e', marginLeft: '4px' }}>*</span></Label>
                <InputLabel
                  style={{ width: '100%', height: '32px', padding: '0 12px', fontSize: '12px', borderRadius: '20px', backgroundColor: '#ebebeb', border: 'none', textAlign: 'center', boxSizing: 'border-box' }}
                  type="text"
                  placeholder="R$ 0,00"
                  value={formatCentDigitsForInput(sellingPrice)}
                  onChange={(e) => handleSellingPriceChange(e.target.value)}
                />
              </PriceFormGroup>

              <PriceFormGroup style={{ flex: '0.8 1 10%', minWidth: '85px' }}>
                <Label>{parsedDiscount !== null && parsedDiscount < 0 ? 'Ajuste (%)' : 'Desconto (%)'}</Label>
                <InputLabel
                  style={{ width: '100%', height: '32px', padding: '0 12px', fontSize: '12px', borderRadius: '20px', backgroundColor: '#ebebeb', border: 'none', textAlign: 'center', boxSizing: 'border-box' }}
                  type="text"
                  placeholder="0%"
                  value={discount}
                  onChange={(e) => handleDiscountChange(e.target.value)}
                />
              </PriceFormGroup>
            </>
          )}

          <PriceFormGroup style={{ flex: '1 1 12%', minWidth: '90px' }}>
            <Label className="block text-gray-500">Preço do Produto</Label>
            <ProductPriceValue>{formatCurrency(selectedPrice || 0)}</ProductPriceValue>
          </PriceFormGroup>
        </FormGrid>

        {selectedProductId && (
          <SpecificationFormGroup>
            <Label>Especificação</Label>
            <SpecificationInput
              type="text"
              value={specification}
              onChange={(e) => setSpecification(e.target.value)}
            />
          </SpecificationFormGroup>
        )}

        {selectedProductId && availableColors.length > 0 && (
          <QuantityTableWrapper ref={quantityWrapperRef}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {availableColors.map((colorItem) => (
                  <Tr key={colorItem.id}>
                    <TdFirst>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={!!selectedColors[colorItem.id]}
                          onChange={() => {
                            setSelectedColors(prev => {
                              const next = { ...prev, [colorItem.id]: !prev[colorItem.id] };
                              if (prev[colorItem.id]) {
                                setQuantities(prevMap => {
                                  const updated = new Map(prevMap);
                                  updated.delete(colorItem.id);
                                  return updated;
                                });
                              }
                              return next;
                            });
                          }}
                        />
                        {colorItem.name}
                      </div>
                    </TdFirst>
                    {availableSizes.map((sizeItem: string, sizeIndex: number) => (
                      <Td key={sizeIndex}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          <span className="size-label" style={{ display: 'inline-block', fontSize: '12px', color: '#696969', minWidth: '26px', textAlign: 'right', visibility: 'hidden', position: 'relative', transition: 'none' }}>{sizeItem}</span>
                          <InputLabel
                            type="number"
                            min="0"
                            disabled={!selectedColors[colorItem.id]}
                            placeholder="0"
                            value={quantities.get(colorItem.id)?.[sizeItem] || ''}
                            onChange={e => {
                              const value = parseInt(e.target.value);
                              setQuantities(prevMap => {
                                const updated = new Map(prevMap);
                                if (!updated.has(colorItem.id)) updated.set(colorItem.id, {});
                                updated.get(colorItem.id)![sizeItem] = isNaN(value) ? 0 : value;
                                return updated;
                              });
                            }}
                          />
                        </div>
                      </Td>
                    ))}
                  </Tr>
                ))}
              </tbody>
            </table>
          </QuantityTableWrapper>
        )}

        {selectedProductId && availableColors.length === 0 && (
          <div style={{ padding: '12px', color: '#888', fontSize: '13px' }}>
            Nenhuma cor disponível. Verifique se o episódio do produto tem cores cadastradas.
          </div>
        )}

        {selectedProductId && availableColors.length > 0 && availableSizes.length === 0 && (
          <div style={{ padding: '12px', color: '#e67e22', fontSize: '13px' }}>
            Produto sem grades cadastradas. Cadastre os tamanhos no produto.
          </div>
        )}



        <Separator />

        <Summary>
          <SummaryItem>
            <SummaryLabel className="text-gray-500">Cores:</SummaryLabel>
            <SummaryValue>{totalSelectedColors}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel className="text-gray-500">Quantidade de peças:</SummaryLabel>
            <SummaryValue>{calculatedTotalQuantity}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel className="text-gray-500">Cadastrado:</SummaryLabel>
            <SummaryValue>{formatCurrency(calculatedTotalSubtotal)}</SummaryValue>
          </SummaryItem>
          {Math.abs(descontoEmReais) >= 0.01 && (
            <SummaryItem>
              <SummaryLabel className="text-gray-500">
                {descontoEmReais < 0 ? 'Ajuste:' : 'Desconto:'}
              </SummaryLabel>
              <SummaryValue>
                {descontoPercentual}
                <SummaryAmount>
                  ({descontoEmReais < 0 ? '+' : '−'}{formatCurrency(Math.abs(descontoEmReais))})
                </SummaryAmount>
              </SummaryValue>
            </SummaryItem>
          )}
          <SummaryItem>
            <SummaryLabel className="text-gray-500">Subtotal:</SummaryLabel>
            <SummaryValue>{formatCurrency(computedValorFinal)}</SummaryValue>
          </SummaryItem>
        </Summary>

        <Separator />

        <ButtonsDiv>
          {isEditing ? (
            <AddButton onClick={handleUpdateProduct}>
              <AddButtonImg src={whitePlus} />
              <AddButtonText>Salvar Alterações</AddButtonText>
            </AddButton>
          ) : (
            <AddButton
              onClick={() => {
                if (isKitMode) {
                  handleAddToKit();
                } else {
                  handleAddStandaloneProduct();
                }
              }}
            >
              <AddButtonImg src={whitePlus} />
              <AddButtonText>
                {isKitMode ? 'Adicionar ao conjunto' : 'Adicionar ao pedido'}
              </AddButtonText>
            </AddButton>
          )}
          <CancelButton onClick={() => {
            resetForm();
            setFormKey(prev => prev + 1);
            onClose?.();
          }}>
            <CancelButtonText>Cancelar</CancelButtonText>
          </CancelButton>
        </ButtonsDiv>
      </Card>
    </>
  );
};

export default AddProductForm;